import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, TextInput, Image, ScrollView, Platform, StatusBar, DeviceEventEmitter, Alert,
} from 'react-native';
import AppModal from './AppModal';
import WardrobeAddProgressModal from './WardrobeAddProgressModal';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors, spacing, radius } from '../theme';
import { confirmPurchaseAndAddToWardrobe } from '../services/PurchaseTrackingService';
import { checkLimit } from '../services/PremiumService';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { getPartnerName } from '../data/partners';
import { detectCategoryFromName } from '../services/CategoryNormalizer';
import { showAppAlert } from './AppDialog';

export default function PurchaseConfirmModal({ product, onClose, onConfirmed }) {
  const insets = useSafeAreaInsets();
  const { user, getValidToken } = useAuth();
  const { lang } = useI18n();
  const en = lang === 'en';
  // v1.13.59 — Sélecteur de taille RETIRÉ de ce popup (sur demande) : les tailles
  // catalogue sont peu fiables. On conserve une valeur par défaut pour
  // l'enregistrement backend, sans l'afficher.
  const [selectedSize] = useState(product?.sizes?.[0] || 'Unique');
  const [pseudo,   setPseudo]   = useState('');
  const [loading,  setLoading]  = useState(false);
  const [done,     setDone]     = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // v1.12.219 — état du flux de progression (uploading → analyzing → done|error)
  const [addProgress, setAddProgress] = useState(null);
  const [addProgressError, setAddProgressError] = useState(null);
  // ═══ v1.8.1 : détection de doublon dans le dressing ═══
  // Avertit l'utilisateur si le produit semble déjà dans son dressing,
  // sans bloquer l'ajout. L'utilisateur peut décider d'ignorer l'avertissement.
  const [duplicateMatch, setDuplicateMatch] = useState(null);

  if (!product) return null;

  const partnerName = getPartnerName(product.partner);

  // ═══ v1.8.1 : check des doublons au montage ═══
  // Compare le produit catalogue avec les items du dressing par :
  //   1. partner_sku exact (le plus fiable — ID unique chez le partenaire)
  //   2. brand + nom normalisé (fallback si partner_sku absent)
  // Stocke le 1er match trouvé dans duplicateMatch pour affichage du bandeau.
  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    (async () => {
      try {
        const uid = user?.id || user?.sub;
        if (!uid) return;
        // Charger le dressing (DB en premier, fallback cache local)
        let wardrobe = [];
        try {
          const { getWardrobe } = require('../services/DatabaseService');
          const w = await getWardrobe(uid, user?.accessToken);
          if (Array.isArray(w)) wardrobe = w;
        } catch {}
        if (wardrobe.length === 0) {
          try {
            const cached = await AsyncStorage.getItem(`outfit_wardrobe_cache_${uid}`);
            if (cached) {
              const parsed = JSON.parse(cached);
              if (parsed?.items && Array.isArray(parsed.items)) wardrobe = parsed.items;
            }
          } catch {}
        }
        if (cancelled) return;

        const productSku = product.zalandoSku || product.zaraId || product.skimId || product.id || '';
        const productNameNorm = String(product.name || '').toLowerCase().trim();
        const productBrandNorm = String(product.brand || '').toLowerCase().trim();

        const match = wardrobe.find(item => {
          // Match 1 : partner_sku identique (le plus fiable)
          if (productSku && item?.partner_sku && String(item.partner_sku) === String(productSku)) {
            return true;
          }
          // Match 2 : brand + nom normalisé (insensible à la casse)
          const itemNameNorm = String(item?.name || '').toLowerCase().trim();
          const itemBrandNorm = String(item?.brand || '').toLowerCase().trim();
          if (productNameNorm && productBrandNorm && itemNameNorm && itemBrandNorm) {
            if (itemNameNorm === productNameNorm && itemBrandNorm === productBrandNorm) {
              return true;
            }
          }
          return false;
        });

        if (match && !cancelled) {
          setDuplicateMatch(match);
          console.log('[PurchaseConfirm] ⚠ doublon détecté:', match.name, '(id=' + match.id + ')');
        }
      } catch (e) {
        console.warn('[PurchaseConfirm] check duplicate failed:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [product?.id, user?.id]);

  // Récupère le nombre d'articles actuels dans le dressing.
  // Source de vérité : DB. Fallback cache local si DB indispo.
  // Le cache local pouvait être stale (items supprimés ailleurs, expiration
  // d'une session précédente, etc.) → bloquait à tort par "limite atteinte".
  async function getWardrobeItemCount() {
    const uid = user?.id || user?.sub;
    if (!uid) return 0;
    // 1. Tenter la DB en premier
    try {
      const { getWardrobe } = require('../services/DatabaseService');
      const wardrobe = await getWardrobe(uid, user?.accessToken);
      if (Array.isArray(wardrobe)) return wardrobe.length;
    } catch (_) {}
    // 2. Fallback cache local
    try {
      const cached = await AsyncStorage.getItem(`outfit_wardrobe_cache_${uid}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.items && Array.isArray(parsed.items)) return parsed.items.length;
      }
    } catch {}
    return 0;
  }

  // v1.12.319 — Override de catégorie si l'user choisit "Mettre dans X"
  //              suite au popup de mismatch (product.category != name-based detection)
  const [overrideCategory, setOverrideCategory] = useState(null);

  // v1.12.319 — Vérifie si la catégorie du produit (DB catalogue) correspond
  //             à ce que la regex détecte du NOM. Si désaccord, on demande
  //             confirmation à l'user avant ajout au dressing.
  const checkCategoryMismatch = () => {
    const currentCat = overrideCategory || product?.category;
    const detected = detectCategoryFromName(product);
    if (!detected || !currentCat) return null;
    if (detected === currentCat) return null;
    return { current: currentCat, detected };
  };

  const handleConfirm = async () => {
    // v1.12.334 — Si non-authentifié, on bloque + popup pour inviter à se connecter.
    //   L'ajout au dressing nécessite un compte (token Cognito) pour persister
    //   l'article et l'attribution cashback.
    if (!user || (!user.accessToken && !user.id && !user.sub)) {
      showAppAlert(
        en ? '🔒 Login required' : '🔒 Connexion requise',
        en
          ? 'You need to be logged in to add an item to your wardrobe.\n\nSign in to start building your wardrobe and earn rewards on every purchase.'
          : "Vous devez être connecté pour ajouter un article à votre dressing.\n\nConnectez-vous pour commencer à construire votre dressing et gagner des récompenses sur vos achats.",
        [
          { text: en ? 'Cancel' : 'Annuler', style: 'cancel' },
          {
            text: en ? 'Sign in' : 'Se connecter',
            style: 'default',
            onPress: () => {
              try { onClose && onClose(); } catch {}
              setTimeout(() => {
                try {
                  const { rootNavigate } = require('../utils/rootNavigation');
                  rootNavigate('Login');
                } catch {}
              }, 150);
            },
          },
        ],
      );
      return;
    }
    // ═══ v1.12.319 — vérification catégorie mismatch (catalogue → dressing) ═══
    const mismatch = checkCategoryMismatch();
    if (mismatch) {
      // v1.12.331 — showAppAlert pour compat web (Alert.alert ne supporte
      //   pas plus de 2 boutons sur RN-Web).
      showAppAlert(
        en ? '⚠️ Unusual category' : '⚠️ Catégorie inhabituelle',
        en
          ? `This item usually belongs to the "${mismatch.detected}" category, not "${mismatch.current}".\n\nAdd anyway in "${mismatch.current}", or use the suggested category?`
          : `Cet article est normalement dans la catégorie "${mismatch.detected}", pas "${mismatch.current}".\n\nVoulez-vous l'ajouter quand même dans "${mismatch.current}", ou utiliser la catégorie suggérée ?`,
        [
          { text: en ? 'Cancel' : 'Annuler', style: 'cancel' },
          {
            text: en ? `Keep "${mismatch.current}"` : `Garder "${mismatch.current}"`,
            style: 'default',
            onPress: () => continueAfterCategory(),
          },
          {
            text: en ? `Use "${mismatch.detected}"` : `Utiliser "${mismatch.detected}"`,
            style: 'default',
            onPress: () => {
              setOverrideCategory(mismatch.detected);
              continueAfterCategory(mismatch.detected);
            },
          },
        ],
      );
      return;
    }
    continueAfterCategory();
  };

  // Étape suivante : check doublon, puis proceedWithAdd
  const continueAfterCategory = (chosenCat = null) => {
    // ═══ v1.8.2 : si doublon détecté, demander confirmation explicite ═══
    if (duplicateMatch) {
      const existingName = duplicateMatch.name || duplicateMatch.brand || (en ? 'Similar item' : 'Article similaire');
      const sizeInfo = duplicateMatch.size
        ? (en ? ` (size ${duplicateMatch.size})` : ` (taille ${duplicateMatch.size})`)
        : '';
      // v1.12.331 — showAppAlert (web-compat) à la place de Alert.alert
      //   pour que la popup multi-boutons fonctionne sur Safari/web.
      showAppAlert(
        en ? '⚠️ Item already in your wardrobe' : '⚠️ Article déjà dans votre dressing',
        en
          ? `You already have:\n\n${existingName}${sizeInfo}\n\nAdd anyway? (e.g. different size)`
          : `Vous avez déjà :\n\n${existingName}${sizeInfo}\n\nVoulez-vous l'ajouter quand même ? (par ex. taille différente)`,
        [
          { text: en ? 'Cancel' : 'Annuler', style: 'cancel' },
          {
            text: en ? 'Add anyway' : 'Ajouter quand même',
            style: 'default',
            onPress: () => proceedWithAdd(chosenCat),
          },
        ],
      );
      return;
    }
    // Pas de doublon → ajout direct
    proceedWithAdd(chosenCat);
  };

  const proceedWithAdd = async (chosenCat = null) => {
    setLoading(true);
    setErrorMsg('');
    try {
      // ═══ GUARD 1 : bloquer si la limite du dressing est atteinte ═══
      const currentCount = await getWardrobeItemCount();
      const wardrobeCheck = await checkLimit('wardrobe', currentCount, user);
      console.log(`[PurchaseConfirm] DRESSING: tier=${wardrobeCheck.tier} count=${currentCount}/${wardrobeCheck.limit} allowed=${wardrobeCheck.allowed}`);
      if (!wardrobeCheck.allowed) {
        console.log(`[PurchaseConfirm] ⛔ bloqué par quota DRESSING: ${wardrobeCheck.current}/${wardrobeCheck.limit}`);
        // Émet l'event générique pour que usePremiumGuard ouvre le popup upgrade
        DeviceEventEmitter.emit('wardrobe_limit_reached', {
          tier:    wardrobeCheck.tier,
          current: wardrobeCheck.current,
          limit:   wardrobeCheck.limit,
          feature: 'add_to_wardrobe',
        });
        onClose();
        return;
      }

      // ═══ GUARD 2 : bloquer l'ajout si quota IA dépassé ═══
      // L'ajout d'un article depuis le catalogue/partenaire déclenche une
      // analyse IA automatique. Si l'user a dépassé sa limite, on bloque
      // l'ajout et on affiche la popup upgrade.
      //
      // ═══ v1.13.59 — Contrôle de quota IA MÊME pour les produits catalogue ═══
      // Avant : le guard était sauté pour les produits catalogue (analyse
      // réutilisée depuis le cron = gratuite) → un user AYANT DÉPASSÉ son quota
      // pouvait quand même ajouter des articles catalogue sans limite. Désormais
      // on applique le contrôle à TOUS les ajouts. checkLimit('ai') est
      // AUTORITATIF (v1.13.57 : suit /ai-usage/status, qui inclut déjà les scans
      // achetés) → un user avec un pack de scans n'est pas bloqué, un free au-delà
      // de sa limite (sans scans) l'est.
      {
        const aiCheck = await checkLimit('ai', 0, user);
        console.log(`[PurchaseConfirm] IA: tier=${aiCheck.tier} used=${aiCheck.current}/${aiCheck.limit} allowed=${aiCheck.allowed}`);
        if (!aiCheck.allowed) {
          console.log(`[PurchaseConfirm] ⛔ bloqué par quota IA: ${aiCheck.current}/${aiCheck.limit}`);
          DeviceEventEmitter.emit('ai_limit_reached', {
            tier:    aiCheck.tier,
            current: aiCheck.current,
            limit:   aiCheck.limit,
            feature: 'add_to_wardrobe',
          });
          onClose();
          return;
        }
      }

      const token = await getValidToken();
      if (!token) {
        setErrorMsg('Token manquant — reconnectez-vous');
        return;
      }
      console.log('PurchaseConfirm: token ok, product=', product?.name, 'size=', selectedSize);
      // v1.12.219 — déclencher la modal de progression (upload → analyze → done)
      setAddProgress('uploading');
      // v1.12.319 — si l'user a override la catégorie après popup mismatch,
      //             on injecte la nouvelle catégorie dans le produit avant l'ajout.
      const effectiveCat = chosenCat || overrideCategory;
      const productForAdd = effectiveCat ? { ...product, category: effectiveCat } : product;
      const result = await confirmPurchaseAndAddToWardrobe(productForAdd, selectedSize, token, {
        pseudo: (pseudo || '').trim() || null,
        onProgress: (phase) => setAddProgress(phase),
      });
      console.log('PurchaseConfirm result:', JSON.stringify(result).slice(0, 200));
      if (result.success) {
        setDone(true);
        setAddProgress('done');
        // v1.12.240+244+247+268 — IA counter :
        //   - Si _aiCounted=true (analyze-item appelé backend) → backend a déjà
        //     incrémenté ai_usage. On skip canUseAIOrIncrement pour éviter double comptage.
        //   - Sinon (cache catalog hit) → on call canUseAIOrIncrement pour +1 backend
        //     (l'analyse a été réutilisée mais on compte quand même 1 ajout).
        console.log('[PurchaseConfirm] 🔍 DEBUG result._aiCounted =', result._aiCounted);
        if (result._aiCounted) {
          console.log('[PurchaseConfirm] ✓ ai_usage déjà incrémenté par /analyze-item — skip');
          // Émet event pour refresh ProfileScreen
          try { DeviceEventEmitter.emit('ai_usage_updated', { source: 'analyze-item' }); } catch {}
        } else {
          try {
            const { canUseAIOrIncrement } = require('../services/PremiumService');
            await canUseAIOrIncrement(user);
          } catch (e) {
            console.warn('[PurchaseConfirm] AI counter sync failed:', e?.message);
            try {
              const uid = user?.id || user?.sub;
              if (uid) {
                const { incrementAIUsage } = require('../services/PremiumService');
                const next = await incrementAIUsage(uid);
                DeviceEventEmitter.emit('ai_usage_updated', { current: next });
              }
            } catch {}
          }
        }
        setTimeout(() => {
          // v1.13.61 — FIX popup "Item added" bloqué : on ferme EXPLICITEMENT la
          // modal de progression (addProgress) avant de fermer le modal parent.
          // Sans ça, le <Modal> natif de progression restait affiché à "done".
          setAddProgress(null);
          setDone(false);
          onConfirmed && onConfirmed(result.item);
          onClose();
        }, 1600);
      } else {
        // ═══ FIX 2026-04 : messages dédiés selon code erreur ═══
        if (result._quotaExceeded || result.error === 'quota_exceeded') {
          // Le popup Premium s'affichera via l'event ai_limit_reached déjà émis
          // par confirmPurchaseAndAddToWardrobe → on ferme juste ce modal.
          setAddProgress(null);
          onClose();
          return;
        }
        if (result.error === 'ai_unavailable') {
          const msg = en
            ? 'AI analysis unavailable — please try again in a moment'
            : "Analyse IA indisponible — réessayez dans quelques instants";
          setAddProgressError(msg);
          setAddProgress('error');
          setErrorMsg(msg);
          return;
        }
        if (result.error === 'photo_rejected') {
          const msg = result._userMessage || (en ? 'Photo not recognized as a clothing item' : 'Photo non reconnue comme un vêtement');
          setAddProgressError(msg);
          setAddProgress('error');
          setErrorMsg(msg);
          return;
        }
        const fallbackErr = en ? 'Failed to add' : 'Erreur ajout';
        setAddProgressError(result.error || fallbackErr);
        setAddProgress('error');
        setErrorMsg(result.error || fallbackErr);
      }
    } catch (e) {
      console.error('PurchaseConfirm error:', e.message);
      setAddProgressError(e.message);
      setAddProgress('error');
      setErrorMsg(e.message);
    } finally {
      setLoading(false);
    }
  };

  const topPad = Platform.OS === 'ios' ? 50 : (StatusBar.currentHeight || 0) + 10;

  return (
    <AppModal
      visible
      transparent
      animationType="slide"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <View style={s.overlay}>
        <View style={[s.sheet, { paddingTop: topPad }]}>

          {/* Header — v1.12.328 : bouton fermer en haut à droite (était à gauche) */}
          <View style={s.header}>
            <View style={{ width: 36 }} />
            <Text style={s.headerTitle}>{en ? 'Add to wardrobe' : 'Ajouter au dressing'}</Text>
            <TouchableOpacity onPress={onClose} style={s.closeBtn} hitSlop={{ top:10,bottom:10,left:10,right:10 }}>
              <Text style={s.closeTxt}>{"✕"}</Text>
            </TouchableOpacity>
          </View>

          <ScrollView
            contentContainerStyle={s.scroll}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Question */}
            <View style={s.questionBox}>
              <Text style={s.questionIcon}>{"🛍"}</Text>
              <Text style={s.questionTitle}>{en ? 'Did you purchase this item?' : 'Avez-vous acheté cet article ?'}</Text>
              <Text style={s.questionSub}>{en ? 'It will be added automatically to your wardrobe' : 'Il sera ajouté automatiquement à votre garde-robe'}</Text>
            </View>

            {/* Produit */}
            <View style={s.productRow}>
              <View style={s.productImg}>
                {product.image
                  ? <Image source={{ uri: product.image }} style={s.productPhoto} resizeMode="cover" />
                  : <Text style={s.productEmoji}>{product.emoji || '👗'}</Text>
                }
              </View>
              <View style={s.productInfo}>
                <Text style={s.productBrand}>{(product.brand || '').toUpperCase()}</Text>
                <Text style={s.productName} numberOfLines={2}>{product.name || ''}</Text>
                <Text style={s.productPrice}>{product.price + " €"}</Text>
              </View>
            </View>

            {/* Pseudo (optionnel) — v1.12.89 ; v1.12.327 — helper retiré */}
            <Text style={s.sizeLabel}>{en ? 'Alias (optional)' : 'Pseudo (optionnel)'}</Text>
            <TextInput
              style={s.pseudoInput}
              placeholder={en ? 'e.g. My favourite, Cosy winter knit…' : 'Ex: Mon préféré, Pull cosy hiver…'}
              placeholderTextColor="#999"
              value={pseudo}
              onChangeText={setPseudo}
              autoCorrect={true}
              autoCapitalize="sentences"
              maxLength={60}
            />
            {/* v1.13.59 — Bloc "Taille commandée" retiré du popup (sur demande) */}
          </ScrollView>

          {/* Footer */}
          <View style={[s.footer, { paddingBottom: Math.max(insets.bottom, 12) + 20 }]}>
            {errorMsg ? <Text style={s.errorTxt}>{errorMsg}</Text> : null}
            {done ? (
              <View style={[s.btn, s.btnGreen]}>
                <Text style={s.btnTxt}>{en ? '✓ Added to wardrobe!' : '✓ Ajouté à la garde-robe !'}</Text>
              </View>
            ) : (
              <View style={s.footerRow}>
                <TouchableOpacity style={[s.btn, s.btnGrey]} onPress={onClose}>
                  <Text style={[s.btnTxt, { color: '#333' }]}>{en ? 'No' : 'Non'}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[s.btn, s.btnBlack, loading && { opacity: 0.6 }]}
                  onPress={handleConfirm}
                  disabled={loading}
                >
                  <Text style={s.btnTxt}>{loading ? (en ? 'Analysing…' : 'Analyse en cours…') : (en ? 'Yes, add' : 'Oui, ajouter')}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

        </View>
      </View>

      {/* v1.12.219 — Modal de progression (upload → analyse → done) */}
      <WardrobeAddProgressModal
        state={addProgress}
        errorMessage={addProgressError}
        onClose={() => {
          setAddProgress(null);
          setAddProgressError(null);
          if (done) {
            // succès : on a déjà appelé onConfirmed/onClose dans le timeout
            return;
          }
        }}
      />
    </AppModal>
  );
}

const s = StyleSheet.create({
  overlay:        { flex:1, backgroundColor:'transparent', justifyContent:'flex-end' },
  sheet:          { backgroundColor:'#fff', borderTopLeftRadius:20, borderTopRightRadius:20, maxHeight:'90%' },
  header:         { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:20, paddingBottom:14, borderBottomWidth:1, borderBottomColor:'#eee' },
  closeBtn:       { width:36, height:36, borderRadius:18, backgroundColor:'#f5f5f5', alignItems:'center', justifyContent:'center' },
  closeTxt:       { fontSize:12, color:'#666', fontWeight:'600' },
  headerTitle:    { fontSize:15, fontWeight:'600', color:'#111' },
  scroll:         { padding:20, gap:16 },
  questionBox:    { backgroundColor:'#F0F7FF', borderRadius:12, padding:16, alignItems:'center', gap:6 },
  questionIcon:   { fontSize:32 },
  questionTitle:  { fontSize:16, fontWeight:'600', color:'#111', textAlign:'center' },
  questionSub:    { fontSize:12, color:'#666', textAlign:'center' },
  productRow:     { flexDirection:'row', gap:12, borderWidth:1, borderColor:'#eee', borderRadius:12, padding:12 },
  productImg:     { width:70, height:84, borderRadius:8, backgroundColor:'#f5f5f5', overflow:'hidden', alignItems:'center', justifyContent:'center' },
  productPhoto:   { width:'100%', height:'100%' },
  productEmoji:   { fontSize:30 },
  productInfo:    { flex:1, justifyContent:'center' },
  productBrand:   { fontSize:9, letterSpacing:1.5, color:'#999', marginBottom:4 },
  productName:    { fontSize:13, fontWeight:'500', color:'#111', lineHeight:18, marginBottom:6 },
  productPrice:   { fontSize:16, fontWeight:'700', color:'#111' },
  sizeLabel:      { fontSize:13, fontWeight:'500', color:'#111' },
  pseudoInput:    { borderWidth:1, borderColor:'#ddd', borderRadius:8, paddingHorizontal:12, paddingVertical:10, fontSize:14, color:'#111', marginTop:6 },
  pseudoHint:     { fontSize:11, color:'#888', marginTop:4, marginBottom:6, lineHeight:15 },
  sizesRow:       { flexDirection:'row', flexWrap:'wrap', gap:8 },
  sizeChip:       { paddingHorizontal:14, paddingVertical:9, borderRadius:8, borderWidth:1.5, borderColor:'#ddd' },
  sizeChipOn:     { borderColor:'#111', backgroundColor:'#111' },
  sizeChipTxt:    { fontSize:13, color:'#555' },
  sizeChipTxtOn:  { color:'#fff', fontWeight:'600' },
  footer:         { padding:20, borderTopWidth:1, borderTopColor:'#eee' },
  footerRow:      { flexDirection:'row', gap:10 },
  btn:            { flex:1, paddingVertical:14, borderRadius:10, alignItems:'center', justifyContent:'center' },
  btnBlack:       { backgroundColor:'#111' },
  btnGrey:        { backgroundColor:'#f5f5f5', borderWidth:1, borderColor:'#ddd' },
  btnGreen:       { backgroundColor:'#2e7d32' },
  btnTxt:         { fontSize:13, fontWeight:'600', color:'#fff' },
  errorTxt:       { fontSize:12, color:'#c62828', textAlign:'center', marginBottom:8 },
});
